import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn, ia } from 'lib/helpers/utility';

import Popover from '../Popovers/Popover/Popover';
import HorizontalScroll from '../HorizontalScroll/HorizontalScroll';

import ToggleItem from './components/ToggleItem';

const ITEM_GAP = 8;
const MORE_BUTTON_WIDTH = 90;

/**
 * @param {import('./lib/propTypes').ToggleSwitchProps} props
 */
const ToggleSwitch = ({
  items = [],
  onClick = () => {},
  active,
  disabled = false,
  count,
  horizontalScroll = false,
  loading,
  className = ''
}) => {
  const [selected, setSelected] = useState(active);
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const itemWidthsRef = useRef([]);
  const availableWidthRef = useRef(0);
  const [isMeasuring, setIsMeasuring] = useState(true);
  const [visibleItems, setVisibleItems] = useState([]);
  const [hiddenItems, setHiddenItems] = useState([]);

  const activeItem = active ?? selected;

  const calculateVisibleItems = useCallback(() => {
    const containerWidth = availableWidthRef.current;
    const widths = itemWidthsRef.current;
    if (widths.length !== items.length) return;

    const rowWidth = (indexes) =>
      indexes.reduce(
        (total, itemIndex, position) => total + widths[itemIndex] + (position ? ITEM_GAP : 0),
        0
      );

    const allIndexes = items.map((_, index) => index);
    if (rowWidth(allIndexes) <= containerWidth) {
      setVisibleItems(items);
      setHiddenItems([]);
      return;
    }

    const availableWidth = containerWidth - MORE_BUTTON_WIDTH;
    const visibleIndexes = [];
    for (const index of allIndexes) {
      if (rowWidth([...visibleIndexes, index]) > availableWidth) break;
      visibleIndexes.push(index);
    }

    const activeIndex = items.findIndex((item) => item.value === activeItem);
    if (activeIndex !== -1 && !visibleIndexes.includes(activeIndex)) {
      visibleIndexes.push(activeIndex);
      while (visibleIndexes.length > 1 && rowWidth(visibleIndexes) > availableWidth) {
        visibleIndexes.splice(visibleIndexes.length - 2, 1);
      }
    }

    setVisibleItems(visibleIndexes.map((index) => items[index]));
    setHiddenItems(items.filter((_, index) => !visibleIndexes.includes(index)));
  }, [items, activeItem]);

  useLayoutEffect(() => {
    if (!horizontalScroll) setIsMeasuring(true);
  }, [horizontalScroll, items]);

  useLayoutEffect(() => {
    if (horizontalScroll || !isMeasuring || !measureRef.current) return;
    itemWidthsRef.current = Array.from(measureRef.current.children, (el) => el.offsetWidth);
    availableWidthRef.current = containerRef.current?.offsetWidth ?? 0;
    setIsMeasuring(false);
  }, [horizontalScroll, isMeasuring, items]);

  useLayoutEffect(() => {
    if (horizontalScroll || isMeasuring) return;
    calculateVisibleItems();
  }, [horizontalScroll, isMeasuring, calculateVisibleItems]);

  useLayoutEffect(() => {
    if (horizontalScroll || !containerRef.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setIsMeasuring(true));
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [horizontalScroll]);

  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) setIsMeasuring(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = (item) => {
    if (disabled || item.value === activeItem) return;

    setSelected(item.value);
    onClick(item);
    item.onClick?.(item);
  };

  if (!ia(items)) return null;

  const others = items.map((item) => ({
    ...item,
    textClassName: 'text-neutral-900',
    ...(item.actions && {
      component: ({ handleClose }) => (
        <Popover
          isFixed
          options={item.actions}
          placement="bottom-end"
          buttonClassName="border-none"
          icon="context-menu-horizontal"
          position="left"
          onOptionClick={handleClose}
          iconColor="neutral"
          iconsShade={700}
          textClassName="text-neutral-900"
          optionIconColor="neutral"
        />
      )
    }),
    onClick: () => handleClick(item)
  }));

  if (horizontalScroll) {
    return (
      <HorizontalScroll
        className={cn('rounded-md', disabled && 'pointer-events-none cursor-not-allowed')}>
        {items.map((item) => (
          <ToggleItem
            key={item.value ?? item.label}
            item={item}
            active={active === item.value}
            disabled={disabled}
            onClick={handleClick}
            count={count}
            loading={loading}
          />
        ))}
      </HorizontalScroll>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex w-full min-w-0 max-w-full items-center gap-2',
        disabled && 'pointer-events-none cursor-not-allowed',
        className
      )}>
      {isMeasuring && (
        <div ref={measureRef} aria-hidden="true" className="invisible flex items-center gap-2">
          {items.map((item) => (
            <div key={item.value ?? item.label} className="flex shrink-0 items-center gap-2">
              <ToggleItem item={item} active={false} onClick={() => {}} />
            </div>
          ))}
        </div>
      )}
      {!isMeasuring &&
        visibleItems.map((item) => (
          <div key={item.value ?? item.label} className="flex shrink-0 items-center gap-2">
            <ToggleItem
              item={item}
              active={active === item.value}
              disabled={disabled || item.disabled}
              onClick={handleClick}
              count={count}
              loading={loading}
            />
          </div>
        ))}
      {!isMeasuring && hiddenItems.length > 0 && (
        <>
          <div className="h-5 w-px shrink-0 rounded-sm bg-neutral-200" />
          <Popover
            buttonTheme="neutral"
            className="h-7"
            isFixed
            buttonClassName="rounded-md bg-transparent !min-h-7 !h-7 !px-2"
            textClassName="text-neutral-800"
            optionGroupLabelClassName="text-neutral-800"
            options={others}
            placement="bottom-end"
            text="More"
            isDropdown
            optionIconSize={16}
            optionIconColor="neutral"
            iconRightColor="neutral-800"
            iconsShade={600}
            icon=""
            menuPortalTarget={document.body}
            panelClassName="overflow-auto max-h-[500px]"
          />
        </>
      )}
    </div>
  );
};

export default ToggleSwitch;
